import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getWindow } from "ssr-window";

import { api } from "@/utils/api";
import { getToken, removeToken } from "@/utils/cookies";
import {
  authStateEnum,
  firstLoginStatusEnum,
  updateStatusEnum,
  existStatusEnum,
  BMMenuStateEnum,
  // 課金-ADD-START
  applicationBillingPlanCodeEnum,
  contractInfoManagementStatus,
  // 課金-ADD-END
} from "@/utils/enum";
import path from "@/utils/path";
import { isPOC } from "@/utils";

// 共通ヘッダー情報取得処理
export const getHeaderInfo = createAsyncThunk(
  path.user.getHeaderInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getHeaderInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// ログアウト処理
export const logout = createAsyncThunk(path.user.logout, (_, { getState }) => {
  const authentication = getState()?.user?.authentication;
  if (authentication) {
    // 削除token
    removeToken();
    return api.user.logout({
      accountId: authentication?.accountId,
      accessToken: authentication?.token,
    });
  }
  return null;
});

// 企業情報取得処理
export const getCompanyInfo = createAsyncThunk(
  path.user.getCompanyInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getCompanyInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// BM利用停止フラグ取得
export const getBmInfo = createAsyncThunk(
  path.user.getBmInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getBmInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// アカウント権限情報取得
export const getMenuAuthorityInfo = createAsyncThunk(
  path.user.getMenuAuthorityInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getMenuAuthorityInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// メニューバッジ取得
export const getMenuBadgeInfo = createAsyncThunk(
  path.user.getMenuBadgeInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getMenuBadgeInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// お知らせバッジ取得
export const getInformationBadgeInfo = createAsyncThunk(
  path.user.getInformationBadgeInfo,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.user.getInformationBadgeInfo({
        accountId: authentication?.accountId,
        accessToken: authentication?.token,
      });
    }
    return null;
  }
);

// coreログアウト処理
export const coreLogout = createAsyncThunk(path.user.coreLogout, () => {
  return api.user.coreLogout();
});

// removeRedis
export const removeRedis = createAsyncThunk(
  path.auth0.removeRedis,
  (_, { getState }) => {
    const authentication = getState()?.user?.authentication;
    if (authentication) {
      return api.auth0.removeRedis({
        key: authentication?.accountId,
        token: authentication?.token,
      });
    }
    return null;
  }
);

export const user = createSlice({
  name: "user",
  initialState: {
    // 認証状態
    authState: authStateEnum.other,
    // authentication
    authentication: undefined,
    // 初回ログインフラグ "0":再ログイン　"1":初回ログイン
    firstLoginStatus: firstLoginStatusEnum.other,
    // 企業プロフィール更新有無取得処理 更新なし:0 更新あり:1
    companyUpdateStatus: updateStatusEnum.other,
    // ユーザープロフィール更新有無取得処理 更新なし:0 更新あり:1
    userProfileUpdateStatus: updateStatusEnum.other,
    // BM利用停止フラグ 0：BM利用停止　1：BM利用中
    bmServiceStatus: existStatusEnum.other,
    // bmServiceStatus: existStatusEnum.existence,
    // マスタ権限アカウント有無フラグ 0:無 1:有
    accountMasterExist: existStatusEnum.other,
    // アカウント権限情報
    menuAuthorityInfo: undefined,
    // menuAuthorityInfo: [
    //   { authorityId: "01", authorityExistence: "1" },
    //   { authorityId: "02", authorityExistence: "1" },
    //   { authorityId: "03", authorityExistence: "1" },
    // ],
    // ユーザ情報
    headerInfo: undefined,
    // 企業プロフィール情報
    companyInfo: undefined,
    // 規約同意情報
    agreementStatusInfo: undefined,
    // メニュー情報
    menuBadgeInfo: undefined,
    // お知らせバッジ情報
    informationBadgeStatus: existStatusEnum.other,
    // business matching menu state
    BMMenuState: BMMenuStateEnum.normal,
    // 課金-ADD-START
    // 契約情報
    contractInfo: undefined,
    // contractInfo: {
    //   applicationBillingPlanCode: "00",
    //   managementStatus: "0",
    //   corporationBmServiceAvailabilityFlag: "1",
    //   isPayment: true,
    // },
    // 0:master
    authorityType: existStatusEnum.other,
    // authorityType: "0",
    // 課金-ADD-END
  },
  reducers: {
    // 認証状態を更新します
    updateAuthState: (state) => {
      const authentication = getToken();
      if (authentication) {
        state.authState = authentication.type;
      } else {
        state.authState = authStateEnum.notCertified;
      }
    },

    // token更新します
    updateAuthentication: (state, action) => {
      state.authentication = action?.payload?.authentication
        ? action?.payload?.authentication
        : getToken();
    },

    // 初回ログインフラグ更新します
    updateFirstLoginStatus: (state, action) => {
      state.firstLoginStatus = action?.payload?.firstLoginStatus;
    },

    // 企業プロフィール更新有無取得処理更新します
    updateCompanyUpdateStatus: (state, action) => {
      state.companyUpdateStatus = action?.payload?.companyUpdateStatus;
    },

    // ユーザープロフィール更新有無取得処理更新します
    updateUserProfileUpdateStatus: (state, action) => {
      state.userProfileUpdateStatus = action?.payload?.userProfileUpdateStatus;
    },

    // マスタ権限アカウント有無フラグ更新します
    updateAccountMasterExist: (state, action) => {
      state.accountMasterExist = action?.payload?.accountMasterExist;
    },

    // 規約同意情報更新します
    updateAgreementStatusInfo: (state, action) => {
      state.agreementStatusInfo = action?.payload?.agreementStatusInfo;
    },

    // business matching menu state update
    updateBMMenuState: (state, action) => {
      state.BMMenuState = action?.payload?.BMMenuState;
    },
  },
  extraReducers: (builder) => {
    // 共通ヘッダー情報取得処理 成功
    builder.addCase(getHeaderInfo.fulfilled, (state, action) => {
      state.headerInfo = action?.payload?.headerInfo;
      // 課金-ADD-START
      if (isPOC()) {
        action.payload.contractInfo = {
          applicationBillingPlanCode: "01",
          managementStatus: "0",
          // applicationBillingPlanCode: "00",
          // managementStatus: "0",
        };
        action.payload.corporationBmServiceAvailabilityFlag = "1";
      }
      state.contractInfo = {
        ...action?.payload?.contractInfo,
        corporationBmServiceAvailabilityFlag:
          action?.payload?.corporationBmServiceAvailabilityFlag,
        isPayment:
          action?.payload?.corporationBmServiceAvailabilityFlag ===
            existStatusEnum.existence &&
          (action?.payload?.contractInfo?.applicationBillingPlanCode ===
            applicationBillingPlanCodeEnum.payment ||
            (action?.payload?.contractInfo?.applicationBillingPlanCode ===
              applicationBillingPlanCodeEnum.free &&
              action?.payload?.contractInfo?.managementStatus ===
                contractInfoManagementStatus.toPayment)),
      };
      getWindow().sitecoreExternal.isPayment = state?.contractInfo?.isPayment;
      // 課金-ADD-END
    });

    // 共通ヘッダー情報取得処理 故障
    builder.addCase(getHeaderInfo.rejected, (state, action) => {
      console.error(
        `getHeaderInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // ログアウト処理 故障
    builder.addCase(logout.rejected, (state, action) => {
      console.error(
        `logout error, state=${JSON.stringify(state)}, action=${JSON.stringify(
          action
        )}`
      );
    });

    // 企業情報取得処理 成功
    builder.addCase(getCompanyInfo.fulfilled, (state, action) => {
      state.companyInfo = action?.payload;
    });

    // 企業情報取得処理 故障
    builder.addCase(getCompanyInfo.rejected, (state, action) => {
      console.error(
        `getCompanyInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // BM利用停止フラグ取得 成功
    builder.addCase(getBmInfo.fulfilled, (state, action) => {
      state.bmServiceStatus = action?.payload?.bmServicePauseFlag;
    });

    // BM利用停止フラグ取得 故障
    builder.addCase(getBmInfo.rejected, (state, action) => {
      console.error(
        `getBmInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // アカウント権限情報取得 成功
    builder.addCase(getMenuAuthorityInfo.fulfilled, (state, action) => {
      state.menuAuthorityInfo = action?.payload?.authorityInfo;
      state.authorityType = action?.payload?.authorityType;
    });

    // アカウント権限情報取得 故障
    builder.addCase(getMenuAuthorityInfo.rejected, (state, action) => {
      console.error(
        `getMenuAuthorityInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // メニュー情報取得 成功
    builder.addCase(getMenuBadgeInfo.fulfilled, (state, action) => {
      getWindow().sitecoreExternal.menuBadgeInfo = action?.payload?.menuInfo;
      state.menuBadgeInfo = action?.payload?.menuInfo;
    });

    // メニュー情報取得 故障
    builder.addCase(getMenuBadgeInfo.rejected, (state, action) => {
      console.error(
        `getMenuBadgeInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // お知らせバッジ取得 成功
    builder.addCase(getInformationBadgeInfo.fulfilled, (state, action) => {
      state.informationBadgeStatus = action?.payload?.newsBadgeFlag;
    });

    // お知らせバッジ取得 故障
    builder.addCase(getInformationBadgeInfo.rejected, (state, action) => {
      console.error(
        `getInformationBadgeInfo error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });

    // coreログアウト処理 故障
    builder.addCase(coreLogout.rejected, (state, action) => {
      console.error(
        `coreLogout error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });
    // removeRedis 故障
    builder.addCase(removeRedis.rejected, (state, action) => {
      console.error(
        `removeRedis error, state=${JSON.stringify(
          state
        )}, action=${JSON.stringify(action)}`
      );
    });
  },
});

export const {
  updateAuthState,
  updateAuthentication,
  updateFirstLoginStatus,
  updateCompanyUpdateStatus,
  updateUserProfileUpdateStatus,
  updateAccountMasterExist,
  updateAgreementStatusInfo,
  updateBMMenuState,
} = user.actions;

export const authState = (state) => state.user.authState;

export const authentication = (state) => state.user.authentication;

export const firstLoginStatus = (state) => state.user.firstLoginStatus;

export const companyUpdateStatus = (state) => state.user.companyUpdateStatus;

export const userProfileUpdateStatus = (state) =>
  state.user.userProfileUpdateStatus;

export const bmServiceStatus = (state) => state.user.bmServiceStatus;

export const accountMasterExist = (state) => state.user.accountMasterExist;

export const menuAuthorityInfo = (state) => state.user.menuAuthorityInfo;

export const headerInfo = (state) => state.user.headerInfo;

export const companyInfo = (state) => state.user.companyInfo;

export const agreementStatusInfo = (state) => state.user.agreementStatusInfo;

export const menuBadgeInfo = (state) => state.user.menuBadgeInfo;

export const informationBadgeStatus = (state) =>
  state.user.informationBadgeStatus;

export const BMMenuState = (state) => state.user.BMMenuState;

// 課金-ADD-START
export const contractInfo = (state) => state.user.contractInfo;

export const authorityType = (state) => state.user.authorityType;
// 課金-ADD-END

export default user.reducer;
